import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Button, Spin, message, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import { getImportBatchDetail, getImportBatchDrafts, confirmImportBatch, updateImportDraft } from 'api/group';
import { EImportRowErrorCode } from 'types/group';
import type { TImportDraftRow } from 'types/group';
import s from './s.module.less';
import klarityLogo from 'assets/logo/KlarityLogo.png';

type Props = {
    batchUid?: string | null;
    // true when the batch comes from the website-import flow: parsing already finished, so skip the
    // parseStatus polling and fetch drafts directly. The website flow also shows an editable Email
    // column and a different title/subtitle.
    isWebsite?: boolean;
    onExit: () => void;
    onConfirmed?: (providerIds: number[]) => void;
};

const POLL_INTERVAL = 2000;

// Rows whose provider already exists in the org — show an "Added" badge and block re-adding.
const isAlreadyAdded = (code?: EImportRowErrorCode) =>
    code === EImportRowErrorCode.EXISTING_USER ||
    code === EImportRowErrorCode.ALREADY_IN_ORG ||
    code === EImportRowErrorCode.ALREADY_PROVIDER;

const nameColumn: ColumnsType<TImportDraftRow>[number] = {
    title: 'Name',
    dataIndex: 'firstName',
    width: 240,
    render: (_: string, record: TImportDraftRow) => (
        <div className="flex justify-between items-center">
            <div className={s.nameCell}>
                <span className={s.nameText}>{`${record.firstName} ${record.lastName}`}</span>
                <span className={s.credentialText}>{record.credential || '-'}</span>
            </div>
            {isAlreadyAdded(record.rowErrorCode) && <span className={s.addedBadge}>Added</span>}
        </div>
    ),
};

const restColumns: ColumnsType<TImportDraftRow> = [
    {
        title: 'Specialty',
        dataIndex: 'specialty',
        width: 200,
        render: (val: string) => val || '-',
    },
    {
        title: 'Service states',
        dataIndex: 'serviceStates',
        width: 160,
        render: (val: string) => val || '-',
    },
    {
        title: 'Payment method',
        dataIndex: 'paymentMethod',
        width: 200,
        render: (val: string) => val || '-',
    },
];

const ReviewProvidersPage: React.FC<Props> = ({ batchUid, isWebsite, onExit, onConfirmed }) => {
    const [loading, setLoading] = useState(true);
    const [parseError, setParseError] = useState<string | null>(null);
    const [draftRows, setDraftRows] = useState<TImportDraftRow[]>([]);
    const [confirming, setConfirming] = useState(false);
    const [savingId, setSavingId] = useState<number | null>(null);
    const pollTimerRef = useRef<number>();
    // last successfully-saved email per row id, used to skip no-op saves on blur
    const savedEmailRef = useRef<Record<number, string>>({});

    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

    useEffect(() => {
        if (!batchUid) return undefined;

        let cancelled = false;

        const runPipeline = async () => {
            setLoading(true);
            setParseError(null);

            try {
                if (!isWebsite) {
                    await new Promise<void>((resolve, reject) => {
                        const poll = async () => {
                            if (cancelled) return resolve();
                            try {
                                const detailRes = await getImportBatchDetail(batchUid);
                                const detail = detailRes?.data?.data;
                                const status = detail?.parseStatus;

                                if (status === 'DONE') {
                                    resolve();
                                } else if (status === 'DONE_EMPTY') {
                                    reject(new Error(detail?.parseError || 'No providers found in the file'));
                                } else if (status === 'FAILED') {
                                    reject(new Error(detail?.parseError || 'File parsing failed'));
                                } else {
                                    pollTimerRef.current = window.setTimeout(poll, POLL_INTERVAL);
                                }
                            } catch (err) {
                                reject(err);
                            }
                        };
                        poll();
                    });
                    if (cancelled) return;
                }

                const draftsRes = await getImportBatchDrafts(batchUid, { size: 200 });
                const rows: TImportDraftRow[] = draftsRes?.data?.data?.records || draftsRes?.data?.data || [];
                if (cancelled) return;

                savedEmailRef.current = rows.reduce<Record<number, string>>((acc, r) => {
                    acc[r.id] = r.email || '';
                    return acc;
                }, {});
                setDraftRows(rows);
                setSelectedKeys(rows.filter((r) => !r.rowErrorCode).map((r) => r.id));
                setLoading(false);
            } catch (err: any) {
                if (!cancelled) {
                    setParseError(err?.message || 'An error occurred while processing the file');
                    setLoading(false);
                }
            }
        };

        runPipeline();

        return () => {
            cancelled = true;
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [batchUid, isWebsite]);

    const handleEmailChange = useCallback((id: number, value: string) => {
        setDraftRows((rows) => rows.map((r) => (r.id === id ? { ...r, email: value } : r)));
    }, []);

    // Save a single email on blur; the backend re-runs pre-check and returns the updated row, so we
    // merge the whole row back (rowErrorCode/rowErrorMsg may change) and sync the selection.
    const handleEmailSave = useCallback(
        async (id: number) => {
            const row = draftRows.find((r) => r.id === id);
            if (!row) return;
            const nextEmail = row.email || '';
            if (nextEmail === savedEmailRef.current[id]) return;

            setSavingId(id);
            try {
                const res = await updateImportDraft(id, { email: nextEmail });
                const updated: TImportDraftRow | undefined = res?.data?.data;
                if (!updated) {
                    return;
                }
                savedEmailRef.current[id] = updated.email || '';
                setDraftRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...updated } : r)));
                setSelectedKeys((keys) => {
                    if (updated.rowErrorCode) {
                        return keys.filter((k) => k !== id);
                    }
                    return keys.includes(id) ? keys : [...keys, id];
                });
            } catch {
                message.error('Failed to update email');
            } finally {
                setSavingId(null);
            }
        },
        [draftRows],
    );

    const columns = useMemo<ColumnsType<TImportDraftRow>>(() => {
        const emailColumn: ColumnsType<TImportDraftRow>[number] = {
            title: 'Email',
            dataIndex: 'email',
            width: 219,
            render: (_: string, record: TImportDraftRow) => {
                // Outside the website flow email is read-only plain text.
                if (!isWebsite) {
                    return <span className={s.emailText}>{record.email || '-'}</span>;
                }
                const hasFixableError = !!record.rowErrorCode;
                return (
                    <div className={s.emailCell}>
                        <Input
                            className={s.emailInput}
                            status={hasFixableError ? 'error' : undefined}
                            value={record.email}
                            placeholder="Enter email"
                            disabled={savingId === record.id}
                            onChange={(e) => handleEmailChange(record.id, e.target.value)}
                            onBlur={() => handleEmailSave(record.id)}
                            onPressEnter={() => handleEmailSave(record.id)}
                        />
                        {hasFixableError && record.rowErrorMsg && (
                            <span className={s.emailError}>{record.rowErrorMsg}</span>
                        )}
                    </div>
                );
            },
        };
        return [nameColumn, emailColumn, ...restColumns];
    }, [isWebsite, savingId, handleEmailChange, handleEmailSave]);

    const handleContinue = useCallback(async () => {
        if (!batchUid || !onConfirmed) return;
        setConfirming(true);
        try {
            const res = await confirmImportBatch(batchUid, {
                rows: selectedKeys.map((key) => ({ rowId: key as number })),
            });
            const providerIds: number[] = res?.data?.data?.providerIdList || [];
            onConfirmed(providerIds);
        } catch {
            message.error('Failed to confirm providers');
        } finally {
            setConfirming(false);
        }
    }, [batchUid, selectedKeys, onConfirmed]);

    const title = isWebsite ? 'Select providers found on your website' : `Review the following providers' profile`;

    return (
        <div className={s.page}>
            <div className={s.topBar}>
                <img src={klarityLogo} alt="Klarity" className={s.logo} />
                <Button className={s.exitBtn} onClick={onExit}>
                    Exit
                </Button>
            </div>

            <div className={s.content}>
                <div className={s.header}>
                    <h2 className={s.title}>{title}</h2>
                    {isWebsite && (
                        <div className={s.subTitle}>
                            <p>
                                We found the following providers on your website. Select the providers you’d like to add
                                to your team.
                            </p>
                            <p>You’ll be able to review and edit their profile details before sending invitations.</p>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className={s.loadingArea}>
                        <Spin size="large" />
                        <p className={s.loadingText}>
                            {isWebsite ? 'Loading providers...' : 'Processing your file...'}
                        </p>
                    </div>
                )}

                {!loading && parseError && (
                    <div className={s.errorArea}>
                        <p className={s.errorTitle}>Failed to process file</p>
                        <p className={s.errorDetail}>{parseError}</p>
                        <Button type="primary" onClick={onExit}>
                            Go back and re-upload
                        </Button>
                    </div>
                )}

                {!loading && !parseError && (
                    <>
                        <div className={s.tableWrap}>
                            <CommonTable<TImportDraftRow>
                                rowKey="id"
                                columns={columns}
                                data={draftRows}
                                pagination={false}
                                bordered
                                rowSelection={{
                                    selectedRowKeys: selectedKeys,
                                    onChange: (keys) => setSelectedKeys(keys),
                                    getCheckboxProps: (record: TImportDraftRow) => ({
                                        disabled: !!record.rowErrorCode,
                                    }),
                                }}
                                rowClassName={(record: TImportDraftRow) => {
                                    if (!record.rowErrorCode) return '';
                                    return isWebsite ? s.errorRow : s.errorRowDimmed;
                                }}
                            />
                        </div>

                        <Button
                            type="primary"
                            className={s.continueBtn}
                            onClick={handleContinue}
                            disabled={selectedKeys.length === 0}
                            loading={confirming}
                        >
                            Continue
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReviewProvidersPage;
